import { useCallback, useContext, useEffect, useState, useMemo } from "react";
import { useRouter } from "next/router";
import { useForm, FormProvider } from "react-hook-form";
import { css } from "@emotion/react";
import { MainTemplate } from "@/components/templates";
import { useSnackBar, Font } from "@moneyforward/mfx_biz_react_components";
import {
  VerifyProvisionalRegistrationTokenWithBizsolMutation,
  useRegisterNewCompanyWithBizsolMutation,
  useRegisterVerifiedCompanyWithBizsolMutation,
  useVerifyProvisionalRegistrationTokenWithBizsolMutation,
} from "@/generated/graphql";
import {
  BizsolRegisterCompanyFormFew,
  BizsolRegisterCompanyForm,
} from "@/components/organisms";
import { AuthContext, SessionInfo } from "@/contexts/AuthContext";
import { PageTitle } from "@/components/molecules";
import {
  ErrorCode,
  errorCodeIsIncluded,
  getErrorMessage,
  moveToErrorPage,
} from "@/libs/errors";
import { ApolloError } from "@apollo/client";
import config from "@/libs/config";
import { setLoginSource, LoginSource } from "@/libs/loginSource";
import { useRegistrationRedirectParams } from "@/components/hooks/useRegistrationRedirectParams";
import useRollbarUtil from "../hooks/useRollbarUtil";
import Rollbar from "rollbar";

type FormValues = {
  lastName: string;
  lastNameKana: string;
  middleName: string;
  middleNameKana: string;
  firstName: string;
  firstNameKana: string;
  email?: string;
  password?: string;
};

/**
 * @summary
 * Anser BizSOL からの新規SSO時の新規事業者＋利用者登録ページ.
 * [Email同期パターン仕様(四国)]: `bizsol/callback` から直接本画面に遷移する registrationCode は URL クエリパラメータから取得 /
 * [Email非同期パターン仕様(京信)]: メール送信した認証リンクから本画面に遷移する registrationCode は verifyProvisionalToken クエリから取得
 * ---
 * [Future] 保守性向上のために、四国と京信で Page コンポーネント自体を分けた方が良い
 */
export const BizsolRegisterCompanyPage = () => {
  const { rollbar } = useRollbarUtil();
  const { setSignInUserSession } = useContext(AuthContext);
  const { notify } = useSnackBar();
  const [verifying, setVerifying] = useState(true);
  const [email, setEmail] = useState("");
  const [registrationCode, setRegistrationCode] = useState("");
  const syncWithBizsolEmail = config.IS_SHIKOKUBANK; // Bizsolに登録しているEmailアドレスをMikatano IDでも利用する

  const router = useRouter();

  // Email同期パターン(四国): code(required), app(optional)
  // Email非同期パターン仕様(京信): token(required), app(optional)
  const { app, code } = router.query;
  const token = useMemo(() => {
    return router.query.token as string;
  }, [router.query.token]);
  const registrationCodeFromQueryParam = Array.isArray(code) ? code[0] : code;

  const methods = useForm<FormValues>({
    reValidateMode: "onBlur",
    defaultValues: {
      middleName: "",
      middleNameKana: "",
    },
  });

  const signIn = async (token: {
    access_token: string;
    id_token: string;
    refresh_token: string;
    token_type: string;
  }) => {
    try {
      const session: SessionInfo = {
        accessToken: token.access_token,
        idToken: token.id_token,
        refreshToken: token.refresh_token,
      };
      await setSignInUserSession(session);
      setLoginSource(LoginSource.bizsol);
      notify("ログインが完了しました");
      // 次のページに遷移する
      // eslint-disable-next-line react-hooks/rules-of-hooks -- TODO: fix to comply with the rules of react-hooks as not following them may lead to unintended side effects
      useRegistrationRedirectParams(router, app);
    } catch (err) {
      rollbar.error("Failed to set session", err);
      notify("ログインに失敗しました");
      setLoginSource(LoginSource.none);
      moveToErrorPage(ErrorCode.BIZSOL_INTERNAL_SERVER_ERROR);
    }
  };

  const [registerNewCompany] = useRegisterNewCompanyWithBizsolMutation({
    onCompleted: (data) => {
      const authToken = data.registerNewCompanyWithBizsol.token;
      if (!authToken) {
        moveToErrorPage(ErrorCode.BIZSOL_INTERNAL_SERVER_ERROR);
        return;
      }
      signIn(authToken);
    },
    onError: (err) => {
      // snackbar で表示すべきエラーの場合は、ここで処理を終える
      // snackbar で表示すべきエラーでない場合は、handleError に処理を委譲する
      const defaultMessage = "新規登録に失敗しました";
      const message = getErrorMessage(err, defaultMessage);
      const isSnackbarError = message !== defaultMessage;
      if (isSnackbarError) {
        notify(message);
        return;
      } else {
        handleError(err, {
          rollbar,
          message: "Failed to register new company with bizsol",
        });
      }
    },
  });

  const [verifyProvisionalRegistrationToken] =
    useVerifyProvisionalRegistrationTokenWithBizsolMutation({
      onCompleted: (
        data: VerifyProvisionalRegistrationTokenWithBizsolMutation
      ) => {
        if (!data.verifyProvisionalRegistrationTokenWithBizsol.result) {
          moveToErrorPage(ErrorCode.INVALID_EMAIL_AUTH_TOKEN);
          return;
        }
        setVerifying(false);
        setEmail(data.verifyProvisionalRegistrationTokenWithBizsol.email);
        setRegistrationCode(
          data.verifyProvisionalRegistrationTokenWithBizsol.registrationCode
        );
      },
      onError: (err) => {
        if (errorCodeIsIncluded(err, ErrorCode.INVALID_EMAIL_AUTH_TOKEN)) {
          moveToErrorPage(ErrorCode.INVALID_EMAIL_AUTH_TOKEN);
          return;
        }
        rollbar.error("Failed to verify provisional registration token", err);
        moveToErrorPage();
      },
    });

  const [registerNewVerifiedCompany] =
    useRegisterVerifiedCompanyWithBizsolMutation({
      onCompleted: (data) => {
        const authToken = data.registerVerifiedCompanyWithBizsol.token;
        if (!token) {
          moveToErrorPage(ErrorCode.BIZSOL_INTERNAL_SERVER_ERROR);
          return;
        }
        signIn(authToken);
      },
      onError: (err) => {
        // snackbar で表示すべきエラーの場合は、ここで処理を終える
        // snackbar で表示すべきエラーでない場合は、handleError に処理を委譲する
        const defaultMessage = "新規登録に失敗しました";
        const message = getErrorMessage(err, defaultMessage);
        const isSnackbarError = message !== defaultMessage;
        if (isSnackbarError) {
          notify(message);
          return;
        } else {
          handleError(err, {
            rollbar,
            message: "Failed to register new verified company with bizsol",
          });
        }
      },
    });

  // Email同期パターン仕様(四国) では、 `/bizsol/callback` から直接新規登録に遷移する
  const handleSubmitSyncWithBizsol = useCallback(
    async (v: FormValues) => {
      const client_id = config.COGNITO_CLIENT_ID;
      await registerNewCompany({
        variables: {
          input: {
            client_id,
            registrationCode: registrationCodeFromQueryParam,
            ...v,
          },
        },
      });
    },
    [registerNewCompany]
  );

  // Email非同期パターン仕様(京信) では認証リンク経由で新規登録が開始されるため 認証tokenの検証を行う
  useEffect(() => {
    if (!token) return;
    verifyProvisionalRegistrationToken({
      variables: { input: { token } },
    });
  }, [token]);

  const handleSubmit = useCallback(
    async (v: FormValues) => {
      const client_id = config.COGNITO_CLIENT_ID;
      await registerNewVerifiedCompany({
        variables: {
          input: {
            client_id,
            registrationCode,
            email,
            ...v,
          },
        },
      });
    },
    [registerNewVerifiedCompany, registrationCode, email]
  );

  if (!router.isReady) {
    return <div></div>;
  }

  return (
    <MainTemplate title={`新規登録`} disableAccountDropDown={true}>
      {!syncWithBizsolEmail && verifying ? (
        <div css={messageWrapper}>
          <Font variant="m" component="p">
            確認中...
          </Font>
        </div>
      ) : (
        <>
          <PageTitle
            mainTitle={`新規登録`}
            subTitle={
              <>
                ユーザー情報を入力して利用規約に同意し
                <br />
                登録を開始してください
              </>
            }
          />
          <FormProvider {...methods}>
            {syncWithBizsolEmail ? (
              <BizsolRegisterCompanyFormFew
                onSubmit={handleSubmitSyncWithBizsol}
              />
            ) : (
              <BizsolRegisterCompanyForm
                onSubmit={handleSubmit}
                email={email}
              />
            )}
          </FormProvider>
        </>
      )}
    </MainTemplate>
  );
};

const handleError = (
  err: ApolloError,
  rollbarInfo: { rollbar: Rollbar; message: string }
) => {
  const { rollbar, message: messageToRollbar } = rollbarInfo;
  const errorList: Array<ErrorCode> = [
    ErrorCode.BIZSOL_USER_IS_NOT_ALLOWED_TO_REGISTER_COMPANY,
    ErrorCode.BIZSOL_COMPANY_ALREADY_REGISTERED,
    ErrorCode.BIZSOL_INVALID_REGISTRATION_TOKEN,
  ];

  errorList.forEach((code) => {
    if (errorCodeIsIncluded(err, code)) {
      moveToErrorPage(code);
      return;
    }
  });

  // 上記にないエラーでもbizsol ボタンのあるエラーページに遷移する
  // username はランダム発番するため、USERNAME_EXISTS はInternalError として扱う
  rollbar.error(messageToRollbar, err);
  moveToErrorPage(ErrorCode.BIZSOL_INTERNAL_SERVER_ERROR);
};

const messageWrapper = css({
  display: "flex",
  justifyContent: "center",
});
